import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { getUserStore, type FrontendId } from '../../store/UserStore';
import { EMAIL_REGEX_FOR_VALIDATION } from '../../constants/constants';
import ResendVerificationLinkButton from './ResendVerificationLinkButton';
import ErrorPane from './ErrorPane';
import { NewPasswordField } from './NewPasswordField';
import { useLocalizedValidationErrorMessages } from '../../hooks/useLocalizedValidationErrorMessages';

interface IRegisterValues {
  username: string;
  email: string;
  password: string;
  passwordConfirm: string;
}

const SignUpForm = observer(
  ({ pathLogin, frontendId }: { pathLogin: string; frontendId: FrontendId }) => {
    const { t } = useTranslation();
    const [agreePersonalData, setAgreePersonalData] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const { error, register, setError } = getUserStore(frontendId);
    const [searchParams] = useSearchParams();

    const registerValidationSchema = Yup.object({
      username: Yup.string()
        .trim()
        .min(3, t('validation.minLength', { length: 3 }))
        .max(255, t('validation.maxLength', { length: 255 }))
        .required(t('login.required.username')),
      email: Yup.string()
        .trim()
        .matches(EMAIL_REGEX_FOR_VALIDATION, t('login.error.wrongEmail'))
        .max(255, t('validation.maxLength', { length: 255 }))
        .required(t('login.required.email')),
      password: Yup.string()
        .trim()
        .max(255, t('validation.maxLength', { length: 255 }))
        .required(t('login.required.password')),
      passwordConfirm: Yup.string()
        .trim()
        .test('passwords-match', t('login.error.passwordMatch'), function (value) {
          return this.parent.password === value;
        }),
    });

    const form = useFormik({
      initialValues: {
        username: searchParams.get('name') || '',
        email: searchParams.get('email') || '',
        password: '',
        passwordConfirm: '',
      },
      validationSchema: registerValidationSchema,
      onSubmit: async (data: IRegisterValues) => {
        const ref = searchParams.get('ref') || null;
        await register(data.username.trim(), data.email.trim(), data.password.trim(), ref);
        setIsSubmitted(true);
      },
      validateOnChange: false,
      validateOnBlur: false,
    });

    const { values, errors, isSubmitting, handleSubmit, handleChange } = form;

    useLocalizedValidationErrorMessages(form);

    return (
      <Stack width={'min(26rem, 80vw)'}>
        <Stack sx={{ mb: 4 }} spacing={1}>
          <Typography fontSize={'2em'}>{t('login.registration')}</Typography>

          <Box />

          <Stack direction="row" spacing={1}>
            <Typography color="text.secondary" variant="body2">
              {t('login.accountQuestion')}&nbsp;
            </Typography>
            <Link to={pathLogin} style={{ textDecoration: 'none' }}>
              <Typography variant="body2" color="secondary">
                {t('login.enter')}
              </Typography>
            </Link>
          </Stack>
        </Stack>

        <>
          {!isSubmitted && (
            <form noValidate onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <TextField
                  autoFocus={!searchParams.get('name')}
                  error={!!errors.username}
                  fullWidth
                  helperText={errors.username}
                  label={t('login.username')}
                  id="username"
                  name="username"
                  type="text"
                  variant="outlined"
                  onChange={handleChange}
                  onFocus={() => setError(undefined)}
                  value={values.username}
                  disabled={isSubmitting}
                />

                <TextField
                  error={!!errors.email}
                  fullWidth
                  helperText={errors.email}
                  label={t('login.email')}
                  id="email"
                  name="email"
                  type="email"
                  variant="outlined"
                  onChange={handleChange}
                  onFocus={() => setError(undefined)}
                  value={values.email}
                  disabled={isSubmitting}
                />

                <NewPasswordField
                  id="password"
                  autoFocus={!!searchParams.get('name')}
                  value={values.password}
                  onChange={handleChange}
                  error={!!errors.password}
                  helperText={errors.password}
                  disabled={isSubmitting}
                  label={t('login.password')}
                />

                <NewPasswordField
                  id="passwordConfirm"
                  value={values.passwordConfirm}
                  onChange={handleChange}
                  error={!!errors.passwordConfirm}
                  helperText={errors.passwordConfirm}
                  disabled={isSubmitting}
                  label={t('login.passwordConfirm')}
                />

                <FormControlLabel
                  control={<Checkbox />}
                  label={t('login.agreeProcessingPersonalData')}
                  value={agreePersonalData}
                  onChange={(_, checked) => setAgreePersonalData(checked)}
                  disabled={isSubmitting}
                />

                <ErrorPane error={error} />
              </Stack>

              <Button
                disabled={!agreePersonalData || isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : undefined}
                fullWidth
                sx={{ mt: 3 }}
                size="large"
                type="submit"
                variant="contained"
              >
                {t('login.register')}
              </Button>
            </form>
          )}

          {isSubmitted && (
            <Stack gap={2}>
              <Typography overflow={'hidden'} textOverflow={'ellipsis'}>
                {t('login.verificationLinkSent', { email: values.email })}
              </Typography>
              <ResendVerificationLinkButton email={values.email} frontendId={frontendId} />
            </Stack>
          )}
        </>
      </Stack>
    );
  }
);

export default SignUpForm;
